import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { ErrorState } from '../../../../shared/components/ErrorState';
import { EmptyState } from '../../../../shared/components/EmptyState';
import { describeApiError } from '../../../../shared/api/errors';
import { useAuth } from '../../../../shared/auth/useAuth';
import { useToast } from '../../../../shared/stores/toast.store';
import { useEmployeeNotasFiscaisHooks } from '../hooks';
import { NotaFiscalUploadDialog } from '../NotaFiscalUploadDialog';
import type { EmployeeNotaFiscalListItem, NotaFiscalStatus } from '../types';

const STATUS_ORDER: NotaFiscalStatus[] = ['SOL', 'PAP', 'APR', 'CON', 'REP', 'NSO', 'CAN'];

/** Chave de ordenação por competência (mais recente primeiro): ano*100 + mês. */
function periodoOrder(n: EmployeeNotaFiscalListItem): number {
  const mm = Number.parseInt(String(n.periodo).split('/')[0] ?? '0', 10);
  return (n.ano ?? 0) * 100 + (Number.isFinite(mm) ? mm : 0);
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY') : iso;
}

function formatBRL(value: number | null | undefined): string {
  return (value ?? 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

function statusChipColor(
  status: NotaFiscalStatus,
): 'default' | 'success' | 'warning' | 'error' | 'info' {
  switch (status) {
    case 'APR':
    case 'CON':
      return 'success';
    case 'REP':
      return 'error';
    case 'SOL':
      return 'warning';
    case 'PAP':
      return 'info';
    default:
      return 'default';
  }
}

function NotaFiscalCard({
  nota,
  onAnexar,
  onBaixar,
  baixarPending,
}: {
  nota: EmployeeNotaFiscalListItem;
  onAnexar: () => void;
  onBaixar: () => void;
  baixarPending: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const podeAnexar = nota.status === 'SOL';
  const podeBaixar =
    !!nota.pdfNome &&
    (nota.status === 'PAP' ||
      nota.status === 'APR' ||
      nota.status === 'REP' ||
      nota.status === 'CON');

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" noWrap>
              {nota.clienteNome}
            </Typography>
            <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
              {nota.periodo}
            </Typography>
            <Typography
              component="div"
              sx={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 600, mt: 0.5 }}
            >
              {formatBRL(nota.valorReceberFormula ?? nota.espelhoValor)}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={t(`features.colaboradores.notasFiscais.status.${nota.status}`)}
            color={statusChipColor(nota.status)}
            variant={nota.status === 'SOL' ? 'filled' : 'outlined'}
          />
        </Stack>

        {nota.status === 'REP' && nota.motivoReprovacao && (
          <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
              {t('features.colaboradores.notasFiscais.meus.motivoReprovacaoLabel')}
            </Typography>
            <Typography variant="body2">{nota.motivoReprovacao}</Typography>
          </Alert>
        )}

        {(nota.dataSolicitacao || nota.dataAnexo || nota.dataAprovacao) && (
          <Stack spacing={0.25} sx={{ mt: 1 }}>
            {nota.dataSolicitacao && (
              <Typography variant="caption" color="text.secondary">
                {t('features.colaboradores.notasFiscais.meus.solicitadaEm', {
                  data: formatDate(nota.dataSolicitacao),
                })}
              </Typography>
            )}
            {nota.dataAnexo && (
              <Typography variant="caption" color="text.secondary">
                {t('features.colaboradores.notasFiscais.meus.anexadaEm', {
                  data: formatDate(nota.dataAnexo),
                })}
              </Typography>
            )}
            {nota.dataAprovacao && (nota.status === 'APR' || nota.status === 'CON') && (
              <Typography variant="caption" color="text.secondary">
                {t('features.colaboradores.notasFiscais.meus.aprovadaEm', {
                  data: formatDate(nota.dataAprovacao),
                })}
              </Typography>
            )}
            {nota.dataAprovacao && nota.status === 'REP' && (
              <Typography variant="caption" color="text.secondary">
                {t('features.colaboradores.notasFiscais.meus.reprovadaEm', {
                  data: formatDate(nota.dataAprovacao),
                })}
              </Typography>
            )}
          </Stack>
        )}

        {(podeAnexar || podeBaixar) && (
          <>
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {podeBaixar && (
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={onBaixar}
                  disabled={baixarPending}
                >
                  {t('features.colaboradores.notasFiscais.meus.baixarPdf')}
                </Button>
              )}
              {podeAnexar && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<UploadFileIcon />}
                  onClick={onAnexar}
                >
                  {t('features.colaboradores.notasFiscais.meus.anexarPdf')}
                </Button>
              )}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function MeusNotasFiscais(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();
  const employeeId = user?.externalId ?? null;
  const [uploadTarget, setUploadTarget] = useState<EmployeeNotaFiscalListItem | null>(null);
  const [anoFiltro, setAnoFiltro] = useState<number | 'all'>('all');

  const hooks = useEmployeeNotasFiscaisHooks(employeeId ?? 0);
  const listQuery = hooks.useList({
    page: 1,
    pageSize: 100,
    sortBy: 'dataCadastro',
    sortOrder: 'desc',
  });
  const downloadFile = hooks.useDownloadFile();

  const rows = useMemo(() => listQuery.data?.data ?? [], [listQuery.data]);
  // KPI conta TODAS as pendentes de envio, independente do filtro de ano —
  // pra nunca esconder uma NF que o colaborador precisa anexar.
  const aguardandoEnvio = useMemo(() => rows.filter((n) => n.status === 'SOL').length, [rows]);

  // Anos disponíveis (desc) pra faixa de filtro. Derivados dos dados carregados.
  const anos = useMemo(() => {
    const set = new Set<number>();
    for (const n of rows) if (n.ano) set.add(n.ano);
    return [...set].sort((a, b) => b - a);
  }, [rows]);

  const visibleRows = useMemo(
    () => (anoFiltro === 'all' ? rows : rows.filter((n) => n.ano === anoFiltro)),
    [rows, anoFiltro],
  );

  const buckets = useMemo(() => {
    const map = new Map<NotaFiscalStatus, EmployeeNotaFiscalListItem[]>();
    for (const s of STATUS_ORDER) map.set(s, []);
    for (const n of visibleRows) (map.get(n.status) ?? []).push(n);
    // Dentro de cada status, competência mais recente primeiro (antes seguia
    // dataCadastro, o que embaralhava os períodos dentro do grupo).
    for (const items of map.values()) items.sort((a, b) => periodoOrder(b) - periodoOrder(a));
    return STATUS_ORDER.map((s) => ({ status: s, items: map.get(s) ?? [] })).filter(
      (b) => b.items.length,
    );
  }, [visibleRows]);

  async function handleBaixar(nota: EmployeeNotaFiscalListItem): Promise<void> {
    try {
      const blob = await downloadFile.mutateAsync(nota.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(
        describeApiError(err) || t('features.colaboradores.notasFiscais.meus.downloadError'),
      );
    }
  }

  if (employeeId === null) {
    return (
      <ErrorState
        description={t('features.colaboradores.notasFiscais.meus.noEmployeeId')}
        onRetry={() => undefined}
      />
    );
  }

  if (listQuery.isLoadingError) {
    return (
      <ErrorState
        description={describeApiError(listQuery.error)}
        onRetry={() => listQuery.refetch()}
      />
    );
  }

  return (
    <Box sx={{ pb: 2 }}>
      <Box
        sx={{
          p: 2,
          mb: 2,
          position: 'sticky',
          top: 56,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="overline" color="text.secondary">
          {t('features.colaboradores.notasFiscais.meus.aguardandoEnvio')}
        </Typography>
        <Typography variant="h3" component="div" sx={{ fontFamily: 'monospace', lineHeight: 1 }}>
          {listQuery.isLoading ? '—' : aguardandoEnvio}
        </Typography>

        {anos.length > 1 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1.5, overflowX: 'auto', pb: 0.5, '::-webkit-scrollbar': { display: 'none' } }}
          >
            <Chip
              label={t('features.colaboradores.notasFiscais.meus.filtroAnoTodos')}
              size="small"
              color={anoFiltro === 'all' ? 'primary' : 'default'}
              variant={anoFiltro === 'all' ? 'filled' : 'outlined'}
              onClick={() => setAnoFiltro('all')}
            />
            {anos.map((ano) => (
              <Chip
                key={ano}
                label={ano}
                size="small"
                color={anoFiltro === ano ? 'primary' : 'default'}
                variant={anoFiltro === ano ? 'filled' : 'outlined'}
                onClick={() => setAnoFiltro(ano)}
              />
            ))}
          </Stack>
        )}
      </Box>

      {!listQuery.isLoading && rows.length === 0 ? (
        <EmptyState description={t('features.colaboradores.notasFiscais.meus.empty')} />
      ) : !listQuery.isLoading && buckets.length === 0 ? (
        <EmptyState description={t('features.colaboradores.notasFiscais.meus.emptyAno')} />
      ) : (
        buckets.map((bucket) => (
          <Box key={bucket.status} sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5, px: 0.5 }}>
              <Typography variant="subtitle2">
                {t(`features.colaboradores.notasFiscais.status.${bucket.status}`)}
              </Typography>
              <Box sx={{ flex: 1 }}>
                <Divider />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {bucket.items.length}
              </Typography>
            </Stack>
            {bucket.items.map((n) => (
              <NotaFiscalCard
                key={n.id}
                nota={n}
                onAnexar={() => setUploadTarget(n)}
                onBaixar={() => void handleBaixar(n)}
                baixarPending={downloadFile.isPending}
              />
            ))}
          </Box>
        ))
      )}

      <NotaFiscalUploadDialog
        open={uploadTarget !== null}
        nota={uploadTarget}
        useUploadFile={hooks.useUploadFile}
        onClose={() => setUploadTarget(null)}
      />
    </Box>
  );
}
